"use client";

import { memo, useEffect, useId, useState } from "react";

// Module-level cache so the same icon URL is fetched once per session.
const svgCache = new Map<string, string>();
const svgInflight = new Map<string, Promise<string>>();

// GCS doesn't send CORS headers — route through the same-origin proxy
// configured in next.config.js so fetch() succeeds.
function rewriteForCors(url: string): string {
  const GCS_PREFIX = "https://storage.googleapis.com/";
  if (url.startsWith(GCS_PREFIX)) {
    return `/_cdn-proxy/gcs/${url.slice(GCS_PREFIX.length)}`;
  }
  return url;
}

function isLikelySvg(url: string): boolean {
  if (url.startsWith("data:image/svg+xml")) return true;
  // Strip query/hash, then check extension. Cheap and accurate for our CDN.
  const pathOnly = url.split(/[?#]/, 1)[0];
  return pathOnly.toLowerCase().endsWith(".svg");
}

/** Decode an inline `data:image/svg+xml[;utf8|;base64],<...>` URI to markup. */
function decodeDataUriSvg(url: string): string {
  const comma = url.indexOf(",");
  const meta = url.slice(0, comma);
  const body = url.slice(comma + 1);
  return meta.includes(";base64") ? atob(body) : decodeURIComponent(body);
}

async function fetchSvg(url: string): Promise<string> {
  const cached = svgCache.get(url);
  if (cached !== undefined) return cached;
  if (url.startsWith("data:image/svg+xml")) {
    const cleaned = decodeDataUriSvg(url).trim();
    svgCache.set(url, cleaned);
    return cleaned;
  }
  const inflight = svgInflight.get(url);
  if (inflight) return inflight;
  const p = fetch(rewriteForCors(url))
    .then((r) => {
      if (!r.ok) throw new Error(`SVG fetch failed: ${r.status}`);
      return r.text();
    })
    .then((text) => {
      const cleaned = text
        .replace(/<\?xml[^?]*\?>/i, "")
        .replace(/<!DOCTYPE[^>]*>/i, "")
        .trim();
      svgCache.set(url, cleaned);
      svgInflight.delete(url);
      return cleaned;
    })
    .catch((err) => {
      svgInflight.delete(url);
      throw err;
    });
  svgInflight.set(url, p);
  return p;
}

function namespaceSvgIds(markup: string, uid: string): string {
  if (!markup.includes("id=")) return markup;
  return markup
    .replace(/(\s)id=(["'])([^"']+)\2/g, `$1id=$2${uid}-$3$2`)
    .replace(/url\((["']?)#([^"')]+)\1\)/g, `url($1#${uid}-$2$1)`)
    .replace(/(\s(?:xlink:)?href=)(["'])#([^"']+)\2/g, `$1$2#${uid}-$3$2`);
}

function processSvg({
  markup,
  className,
  recolorStroke,
  recolorFill,
}: {
  markup: string;
  className?: string;
  recolorStroke: boolean;
  recolorFill: boolean;
}): string {
  let out = markup;
  // Match both double- and single-quoted attrs (inline data: URIs often use
  // single quotes), skipping `none` so fills/strokes meant to be invisible
  // stay that way. currentColor lets the CSS `color` class control the artwork.
  if (recolorStroke) {
    out = out.replace(
      /stroke=(["'])(?!none\b)[^"']*\1/g,
      'stroke="currentColor"'
    );
  }
  if (recolorFill) {
    out = out.replace(/fill=(["'])(?!none\b)[^"']*\1/g, 'fill="currentColor"');
  }
  if (className) {
    out = out.replace(/<svg\b([^>]*)>/, (_match, attrs: string) => {
      if (/\bclass="/.test(attrs)) {
        return `<svg${attrs.replace(/class="([^"]*)"/, `class="$1 ${className}"`)}>`;
      }
      return `<svg${attrs} class="${className}">`;
    });
  }
  return out;
}

interface InlineSvgProps {
  src: string;
  className?: string;
  title?: string;
  /** Replace fill="..." with currentColor. Default false. */
  recolorFill?: boolean;
  /** Replace stroke="..." with currentColor. Default true. */
  recolorStroke?: boolean;
}

/**
 * Inlines a remote SVG into the DOM so its stroke/fill can be themed via the
 * CSS `color` property (Tailwind `text-*` classes). Use when an <img> tag is
 * not enough because you need to recolor the artwork.
 *
 * Falls back to a plain <img> for non-SVG sources (PNG/JPG/etc.) — recolor
 * props are ignored in that case since raster images can't be themed.
 */
export const InlineSvg = memo(function InlineSvg({
  src,
  className,
  title,
  recolorFill = false,
  recolorStroke = true,
}: InlineSvgProps) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const isSvg = isLikelySvg(src);
  const [markup, setMarkup] = useState<string | null>(() =>
    isSvg ? (svgCache.get(src) ?? null) : null
  );

  useEffect(() => {
    if (!isSvg) return;
    const cached = svgCache.get(src);
    if (cached !== undefined) {
      setMarkup(cached);
      return;
    }
    let cancelled = false;
    fetchSvg(src)
      .then((text) => {
        if (!cancelled) setMarkup(text);
      })
      .catch(() => {
        if (!cancelled) setMarkup(null);
      });
    return () => {
      cancelled = true;
    };
  }, [src, isSvg]);

  // Non-SVG: render a plain <img>. Size classes from `className` apply directly.
  if (!isSvg) {
    return (
      <img
        src={src}
        alt={title ?? ""}
        className={className}
        style={{ objectFit: "contain" }}
      />
    );
  }

  if (!markup) {
    return (
      <span
        className={className}
        role={title ? "img" : undefined}
        aria-label={title}
      />
    );
  }

  const processed = processSvg({
    markup: namespaceSvgIds(markup, uid),
    className,
    recolorStroke,
    recolorFill,
  });

  return (
    <span
      className="contents"
      role={title ? "img" : undefined}
      aria-label={title}
      dangerouslySetInnerHTML={{ __html: processed }}
    />
  );
});

export default InlineSvg;
